import { Injectable, computed, inject, signal } from '@angular/core';
import { lastValueFrom } from 'rxjs';

import { AppRoleApiService } from './app-role-api.service';
import { AppRole, AppRoleValue, DepartmentApi } from '../models/api/departments';

@Injectable({
  providedIn: 'root',
})
export class AppRoleService {
  private readonly appRoleApi = inject(AppRoleApiService);

  private readonly _roles = signal<AppRoleValue[] | null>(null);
  private readonly _assignedDepartments = signal<DepartmentApi[]>([]);
  private readonly _loading = signal(false);

  readonly roles = this._roles.asReadonly();
  readonly assignedDepartments = this._assignedDepartments.asReadonly();
  readonly loading = this._loading.asReadonly();

  readonly isOrganizationAdministrator = computed(() => this._roles()?.includes(AppRole.OrganizationAdministrator) ?? false);
  readonly isOrganizationResourceManager = computed(() => this._roles()?.includes(AppRole.OrganizationResourceManager) ?? false);
  readonly isDepartmentResourceManager = computed(() => this._roles()?.includes(AppRole.DepartmentResourceManager) ?? false);
  readonly isDepartmentMember = computed(() => this._roles()?.includes(AppRole.DepartmentMember) ?? false);
  // OrganizationResourceManager is intentionally excluded: ORM has WriteScope=None on the backend.
  // If a new org-level writable role is added, update this predicate and the backend WriteScope logic together.
  readonly canWrite = computed(() => this._roles()?.includes(AppRole.DepartmentResourceManager) ?? false);
  readonly hasMultipleDepartments = computed(() => this._assignedDepartments().length > 1);

  async loadCurrentRole(): Promise<void> {
    this._loading.set(true);
    try {
      const result = await lastValueFrom(this.appRoleApi.getCurrentRole());
      this._roles.set(result.roles);
      this._assignedDepartments.set(result.assignedDepartments);
    } catch {
      this._roles.set([]);
      this._assignedDepartments.set([]);
    } finally {
      this._loading.set(false);
    }
  }
}
